import { get, del, post, put } from 'aws-amplify/api';
import { Dealer } from '../models/dealer.model';
import type { DealerAuditLogResponse } from '../models/dealerAudit.model';
import { CreateDealerRequest, CreateDealerRequestSchema } from '../models/createDealer.model';
import { replaceNullsWithEmpty } from '../utils/utils';
import { getUserToken, getUserTokenOptional } from './util.service';

export const createDealer = async (createDealer: CreateDealerRequest) => {
  const userToken = await getUserToken();
  const createDealerBody = mapToRequest(createDealer);
  const response = await post({
    apiName: 'dealers',
    path: '/dealers',
    options: { body: createDealerBody, headers: { 'X-Auth-Token': `Bearer ${userToken}` } },
  }).response;

  const json = (await response.body.json()) as any;
  return json;
};

export const getDealers = async (filters: {
  name?: string;
  archived?: boolean;
  limit?: number;
  lastKey?: string;
}): Promise<{ items: Dealer[]; lastKey: string }> => {
  const limit = filters.limit ?? 25;
  const lastKey = filters.lastKey ?? '';
  const archived = filters.archived ?? false;
  const name = filters.name ? encodeURIComponent(filters.name) : '';
  const response = await get({
    apiName: 'dealers',
    path: `/dealers?name=${name}&archived=${archived}&limit=${limit}&lastKey=${lastKey}`,
  }).response;
  const json = (await response.body.json()) as any;
  return {
    items: json.items.map((a: any) => mapFromResponse(a)),
    lastKey: json.lastKey,
  };
};

export const getDealer = async (dealerId: string): Promise<Dealer> => {
  const token = await getUserTokenOptional();
  const response = await get({
    apiName: 'dealers',
    path: `/dealers/${dealerId}`,
    ...(token ? { options: { headers: { 'X-Auth-Token': `Bearer ${token}` } } } : {}),
  }).response;
  const json = (await response.body.json()) as any;
  return mapFromResponse(json);
};

export const updateDealer = async (dealer: Dealer): Promise<void> => {
  const userToken = await getUserToken();
  const updateDealerBody = mapToRequest(dealer);
  await put({
    apiName: 'dealers',
    path: `/dealers/${dealer.id}`,
    options: { body: updateDealerBody, headers: { 'X-Auth-Token': `Bearer ${userToken}` } },
  }).response;
};

/** Presigned upload for dealer profile attachments; files go directly to public/ so publicUrl is returned immediately. */
export async function getDealerProfileUploadUrl(
  fileName: string,
  dealerId?: string
): Promise<{ uploadUrl: string; fileKey: string; publicUrl: string }> {
  const response = await post({
    apiName: 'files',
    path: '/files/upload-url',
    options: {
      body: {
        fileName,
        context: 'dealer-docs',
        entityId: dealerId || undefined,
      } as any,
    },
  }).response;
  const json = (await response.body.json()) as { uploadUrl: string; fileKey: string; publicUrl: string };
  return json;
}

export const deleteDealer = async (dealerId: string): Promise<void> => {
  const userToken = await getUserToken();
  await del({
    apiName: 'dealers',
    path: `/dealers/${dealerId}`,
    options: { headers: { 'X-Auth-Token': `Bearer ${userToken}` } },
  }).response;
};

export const archiveDealer = async (dealerId: string): Promise<void> => {
  const userToken = await getUserToken();
  await post({
    apiName: 'dealers',
    path: `/dealers/${dealerId}/archive`,
    options: { headers: { 'X-Auth-Token': `Bearer ${userToken}` } },
  }).response;
};

export const unarchiveDealer = async (dealerId: string): Promise<void> => {
  const userToken = await getUserToken();
  await post({
    apiName: 'dealers',
    path: `/dealers/${dealerId}/unarchive`,
    options: { headers: { 'X-Auth-Token': `Bearer ${userToken}` } },
  }).response;
};

export const getDealerAuditLog = async (
  dealerId: string,
  options?: { limit?: number; lastKey?: Record<string, unknown> }
): Promise<DealerAuditLogResponse> => {
  const params = new URLSearchParams();
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.lastKey !== undefined)
    params.set('lastKey', encodeURIComponent(JSON.stringify(options.lastKey)));
  const query = params.toString();
  const path = query ? `/dealers/${dealerId}/audit-log?${query}` : `/dealers/${dealerId}/audit-log`;
  const response = await get({ apiName: 'dealers', path }).response;
  const json = (await response.body.json()) as unknown as DealerAuditLogResponse;
  return json;
};

const mapFromResponse = (app: any): Dealer => {
  const res: Dealer = {
    ...app,
    documents: app.documents ?? [],
  };
  return replaceNullsWithEmpty(res);
};

const mapToRequest = (dealer: CreateDealerRequest): any => {
  const resParse = CreateDealerRequestSchema.safeParse(dealer);
  if (resParse.success) {
    return resParse.data;
  }

  throw new Error('Validation error');
};
